import { FormApi } from "final-form";
import React from "react";
import { useForm } from "react-final-form";
import { monospaceFontFamily } from "src/providers/theme";
import { makeStyles } from "tss-react/mui";

import { Box, Theme } from "@mui/material";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flexGrow: 1,
        gap: theme.spacing(2),
    },
    advancedModeInput: {
        fontSize: 15,
        fontFamily: monospaceFontFamily,
        marginTop: theme.spacing(2),
        padding: theme.spacing(1, 2),
        resize: "both",
        width: "100%",
        minHeight: "400px",
        overflowY: "auto",
        color: theme.palette.emteria.text.primary,
        backgroundColor: theme.palette.emteria.background.default,
    },
    advancedModeInputNoResize: {
        fontSize: 15,
        fontFamily: monospaceFontFamily,
        marginTop: theme.spacing(2),
        padding: theme.spacing(1, 2),
        resize: "none",
        width: "100%",
        minHeight: "400px",
        overflowY: "auto",
        color: theme.palette.emteria.text.primary,
        backgroundColor: theme.palette.emteria.background.default,
    },
    redText: { color: theme.palette.emteria.error },
    reBorder: { borderColor: theme.palette.emteria.error },
    alert: { minWidth: 500 },
    columnReverse: { flexDirection: "column-reverse" },
}));

type AlertProps = {
    note?: string | React.ReactNode;
    error?: string | React.ReactNode | null;
    warning?: string | React.ReactNode | null;
};
const AlertSection = (props: AlertProps) => {
    const { classes } = useStyles();

    if (props.error) {
        return (
            <CustomAlert className={classes.alert} severity="error">
                {props.error}
            </CustomAlert>
        );
    }
    if (props.warning) {
        return (
            <CustomAlert className={classes.alert} severity="warning">
                {props.warning}
            </CustomAlert>
        );
    }
    if (props.note) {
        return (
            <CustomAlert className={classes.alert} severity="info">
                {props.note}
            </CustomAlert>
        );
    }
    return null;
};

type Props = React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement> &
    Partial<AlertProps> & { disableResize?: boolean; containerStyle?: React.CSSProperties };
export const JsonTextarea = ({ containerStyle, ...props }: Props) => {
    const { classes, cx } = useStyles();

    return (
        <Box className={classes.container} style={containerStyle}>
            <textarea
                {...props}
                data-testid="json-textarea"
                spellCheck={false}
                className={
                    props.error
                        ? cx(
                              props.disableResize ? classes.advancedModeInputNoResize : classes.advancedModeInput,
                              classes.reBorder,
                              classes.redText,
                          )
                        : props.disableResize
                          ? classes.advancedModeInputNoResize
                          : classes.advancedModeInput
                }
            />
        </Box>
    );
};

type ControlledProps<T> = AlertProps & {
    defaultValue: unknown;
    onChange: (value: string, form: FormApi<T, Partial<T>>) => void;
    style?: React.CSSProperties;
    disableResize?: boolean;
    alertPosition?: "top" | "bottom";
};
export const ControlledJsonTextarea = <T,>({ alertPosition = "top", ...props }: ControlledProps<T>) => {
    const defaultValue = JSON.stringify(props.defaultValue, null, 2);
    const [edit, setEdit] = React.useState<{ base: string; value: string } | null>(null);
    const value = edit?.base === defaultValue ? edit.value : defaultValue;
    const { classes, cx } = useStyles();

    const form = useForm<T>();

    const onChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
        setEdit({ base: defaultValue, value: e.currentTarget.value });
        props.onChange(e.currentTarget.value, form);
    };

    return (
        <Box className={cx(classes.container, alertPosition === "bottom" && classes.columnReverse)} style={props.style}>
            <AlertSection {...props} />
            <textarea
                data-testid="json-textarea"
                spellCheck={false}
                onChange={onChange}
                value={value}
                className={
                    props.error
                        ? cx(
                              props.disableResize ? classes.advancedModeInputNoResize : classes.advancedModeInput,
                              classes.reBorder,
                              classes.redText,
                          )
                        : props.disableResize
                          ? classes.advancedModeInputNoResize
                          : classes.advancedModeInput
                }
            />
        </Box>
    );
};
